import { Record } from '../types';

export const getId = <T extends Record>(i: T): string => i.id;
export const getID = getId;

export const isRecordInSet = <T extends Record>(records: T[], record: T) =>
  records.map(getId).includes(record.id as string);

export function getIDs<T = string[]>(
  items?: { id: string }[],
  default_value?: T
): string[] | T {
  return items?.map((item) => item.id) || default_value || [];
}

/** Removes record from a list by its ID */
export const removeByID = <T extends Record>(records: T[], id: string): T[] =>
  records.filter((r) => r.id !== id);

export const findByID = <T extends Record>(
  records: T[] | undefined,
  id?: string
): T | undefined => (id ? records?.find((i) => i.id === id) : undefined);

export const setByID = <T extends Record>(
  records: T[] | undefined,
  record: T
): T[] => {
  return (records || []).map((r) => (r.id === record.id ? record : r));
};

export const updateByID = <T extends Record>(
  records: T[] | undefined,
  record: Partial<T>
): T[] => {
  return (records || []).map((r) =>
    r.id === record.id ? { ...r, ...record } : r
  );
};

export const getRecordSetDifference = <T extends Record>(
  originalSet: T[],
  newSet: T[]
) => {
  const newSetIDs = newSet.map(getId);
  const originalSetIDs = originalSet.map(getId);
  const added = newSet.filter((r) => !originalSetIDs.includes(r.id));
  const removed = originalSet.filter((r) => !newSetIDs.includes(r.id));

  const updated = newSet.filter(
    (i) =>
      originalSetIDs.includes(i.id) &&
      JSON.stringify(i) !==
        JSON.stringify(originalSet.find((r) => r.id === i.id))
  );

  return { added, removed, updated };
};
